import { Box, InputAdornment, TextField, Tooltip, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Editable } from "lib/@components/editable"
import noop from "lib/noop"
import { SummaryDataChanged } from "event-definitions"
import { MinHeightBox } from "lib/@components/minHeightBox"
import { HighlightBox } from "lib/@components/highlightBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useRef, useState } from "react"
import { useRefresh } from "lib/@hooks/useRefresh"
import { LocalizedMessage } from "lib/localization/localized-message"
import { TimeDurationTooltipBody } from "./time-duration-tooltip-body"
import { RegimeDraftLockTooltip } from "routes/regime/regime-draft-locking/regime-draft-lock-tooltip"
import { getDurationParts, getDurationLabel } from "./time-utils"

export function Time(props) {
    const { task, editMode, timeLabelSx } = useBoundContext()
    if (!task) return null
    const amount = task.minutes * 1000 * 60
    const isMobile = false
    if (amount === 0 && !editMode) {
        return null
    }
    const { wholeMinutes: minutes, roundedMinutes: rounded, seconds } = getDurationParts(task.minutes)

    return (
        /* (editMode || task.$showMinutes || inComparison)  && */ <If
            truthy={isMobile}
            then={
                <HighlightBox path="minutes">
                    <MinHeightBox className="time" data-cy="task-time" data-comment-part="time">
                        <Typography variant="caption">
                            {minutes}:{seconds.toString().padLeft(2, "0")}
                        </Typography>
                    </MinHeightBox>
                </HighlightBox>
            }
            else={
                <MinHeightBox className="time" data-cy="task-time" data-comment-part="time" ml={1} minWidth="3.3em">
                    <Editable editor={<TimeEditor {...props} />}>
                        <HighlightBox path="minutes">
                            <Tooltip
                                enterDelay={300}
                                enterNextDelay={300}
                                title={
                                    <TimeDurationTooltipBody minutes={minutes} seconds={seconds} rounded={rounded} />
                                }
                            >
                                <Box sx={timeLabelSx} px={Number.isNaN(minutes) ? 1 : 0}>
                                    {getDurationLabel({ wholeMinutes: minutes, roundedMinutes: rounded, seconds })}
                                </Box>
                            </Tooltip>
                        </HighlightBox>
                    </Editable>
                </MinHeightBox>
            }
        />
    )
}

function convertToTime(v) {
    if (typeof v === "string") return v
    const { wholeMinutes, seconds } = getDurationParts(v)
    return `${wholeMinutes}:${seconds.toString().padLeft(2, "0")}`
}

function convertFromTime(v = "") {
    try {
        if (!v.trim().length) return 0
        const parts = v.split(":").map((v) => +v)
        if (parts.some((p) => Number.isNaN(p))) return 0
        parts[1] = parts[1] || 0
        return parts[0] + parts[1] / 60.0
    } catch (e) {
        return 0
    }
}

export function TimeEditor({ cancel = noop, sx, ...props }) {
    const refresh = useRefresh()
    const { payload, target, task } = useBoundContext()
    const [time, setTime] = useBoundValue("minutes")
    const tailoring = !!payload && target !== task
    // The payload is a delta store: no stored minutes means "inherit from the anchored schedule
    // version". The field must still SHOW that inherited value — the editors no longer seed copies
    // of it into the payload (that seeding minted the machine-written pins the timing repair
    // removes), so it is displayed from the task and persisted only when the user edits it.
    // Captured ONCE, while there is no override: extend-schedule overlays payload minutes onto the
    // extended task, so task.minutes aliases the override as soon as one exists — the same capture
    // rule as useFrequencyOverride. null means "override already stored when the editor opened,
    // inherited unknown".
    const inheritedRef = useRef()
    if (inheritedRef.current === undefined) {
        inheritedRef.current =
            tailoring && (time == null || time === "") && Number.isFinite(+task?.minutes) ? +task.minutes : null
    }
    const inherited = inheritedRef.current
    // The one guarantee tailoring blur gives: nothing reaches the payload unless the USER edited
    // the field. A value comparison alone cannot give it — the field displays m:s-ROUNDED text, so
    // re-parsing an untouched "4:17" yields 4.2833… for a stored 4.2857…, which reads as "changed".
    const editedRef = useRef(false)
    const [current, setCurrent] = useState(openingValue)

    return (
        <RegimeDraftLockTooltip>
            <TextField
                className="bind minutes"
                variant="outlined"
                label="Duration"
                value={current}
                {...props}
                onChange={handleChange}
                onKeyDown={preventNonTimeCharacters}
                sx={{ width: 80, ...sx }}
                onBlur={blur}
                field="minutes"
                size="small"
                data-cy="edit-task-duration"
                InputProps={{
                    sx: { pr: 0.2 },
                    endAdornment: (
                        <InputAdornment position="end">
                            <Tooltip title={<LocalizedMessage template="Minutes : Seconds" />} arrow>
                                <Typography variant="caption" sx={{ pr: 0.2, color: "primary.main" }}>
                                    m:s
                                </Typography>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
            />
        </RegimeDraftLockTooltip>
    )

    // Every keystroke marks the field as user-edited — the tailoring blur refuses to write
    // anything without it (see editedRef above). The FIQ-1009 commitOnChange branch that also
    // lived here was reverted on develop along with both callers that passed the prop.
    function handleChange(e) {
        editedRef.current = true
        setCurrent(e.target.value)
    }

    function preventNonTimeCharacters(e) {
        const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Escape"]
        const isNumberOrColon = /^[0-9:]$/.test(e.key)

        if (!isNumberOrColon && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
            e.preventDefault()
        }
    }

    function blur() {
        if (tailoring) {
            blurTailoring()
            return
        }
        const converted = convertFromTime(current)
        setTime(converted)
        setCurrent(convertToTime(converted))
        finish(true)
    }

    function blurTailoring() {
        // Untouched blur writes NOTHING, ever — see editedRef above. Tabbing through rows must
        // never turn the DISPLAYED inherited value into a stored pin.
        if (!editedRef.current) {
            finish(false)
            return
        }
        editedRef.current = false
        const hasOverride = time != null && time !== ""
        // An emptied duration means "remove the override and inherit from the schedule". Writing
        // through convertFromTime here would coerce the empty field to 0 and silently create a
        // zero-minute override the user never typed. `tailoring`'s `target !== task` (mirroring
        // useFrequencyOverride's guard) confirms the bound target really IS the payload task, not
        // the master — without it, a context that merely carries a payload while bound to the
        // master would delete the MASTER's minutes instead.
        if (!current.trim().length) {
            if (hasOverride) setTime(undefined)
            setCurrent(inherited != null ? convertToTime(inherited) : "")
            finish(hasOverride)
            return
        }
        const converted = convertFromTime(current)
        // Values are compared as DISPLAYED (m:s), not numerically: the user can only ever type
        // what the field can show, so typing the same m:s the inherited value renders as IS the
        // user saying "inherit" — a numeric compare would call rounded 4:17 (4.2833…) an edit of
        // inherited 4.2857… and mint a drifted pin. A duration equal to what the task inherits is
        // not tailoring, so nothing is persisted — and an override typed back to the inherited
        // value is removed. (A row that OPENED with an override captured no inherited value, so an
        // existing override is only ever removed by clearing the field, exactly as frequencies.)
        if (inherited != null && convertToTime(converted) === convertToTime(+inherited)) {
            if (hasOverride) setTime(undefined)
            setCurrent(convertToTime(converted))
            finish(hasOverride)
            return
        }
        // An existing override retyped as the same m:s it already displays: nothing to persist —
        // rewriting would replace the stored value with its own rounded re-parse.
        if (hasOverride && convertToTime(converted) === convertToTime(+time)) {
            setCurrent(convertToTime(converted))
            finish(false)
            return
        }
        setTime(converted)
        setCurrent(convertToTime(converted))
        finish(true)
    }

    function finish(changed) {
        cancel()
        if (changed) SummaryDataChanged.raiseOnce()
        refresh()
    }

    // A persisted value opens formatted; with NO stored value a tailoring field opens showing the
    // INHERITED duration (display only — see blurTailoring), while the MASTER editors (the 0U
    // wizard, authoring) keep opening blank for their zero "not set yet"
    // placeholder: rendering it would leave "0:00" in the field for typed input to APPEND to —
    // typing 5:00 into a prefilled 0:00 makes "0:005:00", which parses as five seconds.
    function openingValue() {
        if (time == null || time === "") return inherited != null ? convertToTime(inherited) : ""
        if (!tailoring && !time) return ""
        return convertToTime(time)
    }
}
